import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE, AUTH_AUDIENCE } from '../config/env';
import { directionForLanguage, type Direction } from './direction';
import { DEFAULT_LANGUAGE, normalizeLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from './languages';

/**
 * Holds the active language at the top of the tree so direction reaches the
 * theme layer, and keeps it in step with the account (`/users/me
 * preferredLanguage`) — the same source of truth the mobile app uses, so a
 * patient who uses both sees one language.
 *
 * Resolution on load: what this browser last used → `?language=` → the
 * browser's language → English. Once signed in, the account's stored
 * preference wins, unless the patient chose a language on the sign-in screen
 * seconds ago — that deliberate choice is saved back to the account instead
 * (matching the app's `chooseLanguage` / `claimPendingChoice` behaviour).
 */

const STORAGE_KEY = 'elenthor.portal.language';

type LanguageState = {
  language: SupportedLanguage;
  direction: Direction;
  isRTL: boolean;
  /** Languages the clinic has enabled; the picker offers only these. */
  enabledLanguages: SupportedLanguage[];
  /** A deliberate choice: applies immediately, persists locally and on the account when signed in. */
  setLanguage: (next: SupportedLanguage) => Promise<void>;
  saving: boolean;
  saveError: boolean;
};

const LanguageContext = createContext<LanguageState | null>(null);

function readStored(): SupportedLanguage | null {
  try {
    return normalizeLanguage(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStored(language: SupportedLanguage) {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    /* ignore */
  }
}

function initialLanguage(): SupportedLanguage {
  const fromQuery = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      // `lang` / `locale` are aliases older links used.
      return normalizeLanguage(params.get('language') ?? params.get('lang') ?? params.get('locale'));
    } catch {
      return null;
    }
  })();
  return (
    fromQuery ??
    readStored() ??
    (typeof navigator !== 'undefined' ? normalizeLanguage(navigator.language) : null) ??
    DEFAULT_LANGUAGE
  );
}

function applyDocumentDirection(language: SupportedLanguage, direction: Direction) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (root.lang !== language) root.lang = language;
  if (root.dir !== direction) root.dir = direction;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [language, setLanguageState] = useState<SupportedLanguage>(initialLanguage);
  const [enabledLanguages, setEnabledLanguages] = useState<SupportedLanguage[]>([...SUPPORTED_LANGUAGES]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  // A language picked before sign-in. Read once after the account loads.
  const pendingChoice = useRef<SupportedLanguage | null>(null);

  const direction = directionForLanguage(language);
  // During render, not in an effect, so the first paint is already aligned.
  applyDocumentDirection(language, direction);

  const authedFetch = useCallback(
    async (init?: RequestInit) => {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
      return fetch(`${API_BASE}/users/me`, {
        ...init,
        headers: { ...(init?.headers || {}), Authorization: `Bearer ${token}` },
      });
    },
    [getAccessTokenSilently],
  );

  const persistToAccount = useCallback(
    async (next: SupportedLanguage) => {
      setSaving(true);
      setSaveError(false);
      try {
        const res = await authedFetch({
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferredLanguage: next }),
        });
        if (!res.ok) throw new Error(`PATCH /users/me ${res.status}`);
      } catch {
        setSaveError(true);
      } finally {
        setSaving(false);
      }
    },
    [authedFetch],
  );

  // Sync with the account once signed in.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch();
        if (!res.ok || cancelled) return;
        const payload = await res.json();
        const enabled = Array.isArray(payload?.organization?.enabledLanguages)
          ? (payload.organization.enabledLanguages.map(normalizeLanguage).filter(Boolean) as SupportedLanguage[])
          : [];
        if (enabled.length) setEnabledLanguages(enabled);

        const chosen = pendingChoice.current;
        pendingChoice.current = null;
        if (chosen) {
          // The deliberate pre-auth choice wins and becomes the account's.
          if (payload?.preferredLanguage !== chosen) await persistToAccount(chosen);
          return;
        }
        const stored = normalizeLanguage(payload?.preferredLanguage) ?? normalizeLanguage(payload?.organization?.effectiveLanguage);
        if (stored && !cancelled) {
          setLanguageState(stored);
          writeStored(stored);
        }
      } catch {
        // Keep the locally resolved language.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, authedFetch, persistToAccount]);

  const setLanguage = useCallback(
    async (next: SupportedLanguage) => {
      setLanguageState(next);
      writeStored(next);
      if (isAuthenticated) await persistToAccount(next);
      else pendingChoice.current = next;
    },
    [isAuthenticated, persistToAccount],
  );

  const value = useMemo<LanguageState>(
    () => ({ language, direction, isRTL: direction === 'rtl', enabledLanguages, setLanguage, saving, saveError }),
    [language, direction, enabledLanguages, setLanguage, saving, saveError],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageState {
  const value = useContext(LanguageContext);
  if (!value) throw new Error('useLanguage must be used within a LanguageProvider.');
  return value;
}
